import React, { Fragment, PureComponent, ReactNode } from 'react';
import { AutoComplete } from 'antd';
import { BaseSelectRef } from 'rc-select/lib/index';
import debounce from 'lodash/debounce';
import onContainerClick from '../../../../../pages/prescriptionPad/onClickContainer';
import { EkaFocosableElement } from '../../../../common/types/EkaFocusable';

export interface OptionTypeBaseObj {
    id: string;
    [key: string]: any;
}

export type OptionTypeBase = OptionTypeBaseObj | string;

interface State<OptionType extends OptionTypeBase> {
    options: OptionType[];
    menuIsOpen: boolean;
    value?: string;
}

export interface AntAutoCompleteProps<OptionType extends OptionTypeBase> {
    autoFocus?: boolean;
    setRef?: (ref: EkaFocosableElement) => void;
    placeholder: string;
    selectedValue?: OptionType;
    onSearch: (value: string) => Promise<OptionType[]>;
    onSelect: (option?: OptionType, searchString?: string) => void;
    createOption?: (text: string) => OptionType;
    debounceDelay?: number;
    getLabelToCompare: (option: OptionType) => string;
    onChange?: (value: string, option: OptionType) => void;
    onBlur?: (e: React.FocusEvent<HTMLInputElement>) => void;
    customInput?: ReactNode;
    customDropdownClass?: string;
    customDropdownWidth?: number;
    optionsRenderer?: (option: OptionType) => ReactNode;
    /**
     * @deprecated Use containerSelectorToAttachDropdown
     */
    containerIdToAttachDropdown?: string;
    containerSelectorToAttachDropdown?: string;
    className?: string;
    placement?: 'bottomLeft' | 'bottomRight' | 'topLeft' | 'topRight';
    disabled?: boolean;
    clearAfterSelect?: boolean;
    customStyle?: React.CSSProperties;
}

class AntAutoCompleteV2<T extends OptionTypeBase> extends PureComponent<
    AntAutoCompleteProps<T>,
    State<T>
> {
    autoComplete: BaseSelectRef | undefined = undefined;
    isSelected = true;

    state: State<T> = {
        options: [],
        menuIsOpen: false,
        value: this.props.selectedValue
            ? this.props.getLabelToCompare(this.props.selectedValue)
            : undefined,
    };

    componentDidUpdate = (prevPops: AntAutoCompleteProps<T>): void => {
        if (
            (typeof this.props.selectedValue === 'string' &&
                typeof prevPops.selectedValue === 'string' &&
                this.props.selectedValue !== prevPops.selectedValue) ||
            (typeof this.props.selectedValue === 'object' &&
                typeof prevPops.selectedValue === 'object' &&
                this.props.selectedValue?.id !== prevPops.selectedValue?.id)
        ) {
            this.setState({
                value: this.props.selectedValue
                    ? this.props.getLabelToCompare(this.props.selectedValue)
                    : undefined,
            });
        }
    };

    constructor(props: AntAutoCompleteProps<T>) {
        super(props);
    }

    onSelect = (_: string, { key, value }: { key: string; value: string }): void => {
        const { getLabelToCompare } = this.props;

        this.setState({
            menuIsOpen: false,
        });

        const lowerCasedValue = value.toLowerCase();
        const option = this.state.options.find((o) => {
            // console.log('onSelect', getLabelToCompare(o).toLowerCase(), lowerCasedValue);
            return getLabelToCompare(o).toLowerCase() === lowerCasedValue;
        });

        if (!option) {
            return;
        }

        this.isSelected = true;

        this.autoComplete?.blur();

        if (this.props.clearAfterSelect) {
            this.setState({ value: undefined, options: [] });
        }

        this.props.onSelect(option, this.state.value);
        return;
    };

    handleSearch = (value: string): void => {
        this.props.onSearch(value).then((res) => {
            this.setState({
                options: res,
            });
        });

        return;
    };

    onSearch = debounce(this.handleSearch, this.props.debounceDelay);

    onBlur = (e: React.FocusEvent<HTMLInputElement>): void => {
        onContainerClick();

        this.setState({
            menuIsOpen: false,
        });

        if (this.isSelected) {
            this.props.onBlur && this.props.onBlur(e);
            return;
        }

        const { createOption, getLabelToCompare } = this.props;

        const value = e.target.value;

        const lowerCasedValue = value.toLowerCase();

        let option = this.state.options.find(
            (o) => getLabelToCompare(o).toLowerCase() === lowerCasedValue,
        );

        if (!option && value && createOption) {
            option = createOption(value);
        }

        if (!option) {
            if (!value) {
                this.onClear();
            } else {
                const newvalue = this.props.selectedValue
                    ? this.props.getLabelToCompare(this.props.selectedValue)
                    : undefined;
                this.setState({
                    value: newvalue,
                });
                this.handleSearch(newvalue || '');
                this.props.onSelect(option);
            }
        } else {
            this.props.onSelect(option);
        }

        this.props.onBlur && this.props.onBlur(e);
    };

    onFocus = (e: React.FocusEvent<HTMLInputElement>): void => {
        this.handleSearch(e.target.value);
        this.setState({
            menuIsOpen: true,
        });
        e.target.select();
    };

    ref = (ref: BaseSelectRef): void => {
        const { setRef } = this.props;
        this.autoComplete = ref;
        ref && setRef && setRef(ref);
    };

    // TODO
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    onChange = (value: string, option): void => {
        this.isSelected = false;
        this.setState({
            value,
        });
        this.props.onChange && this.props.onChange(value, option);
    };

    onClear = (): void => {
        this.props.onSelect();

        this.isSelected = true;

        this.handleSearch('');
    };

    onKeyDown = (e: React.KeyboardEvent<HTMLDivElement>): void => {
        if (e.key === 'Escape') {
            this.autoComplete?.blur();
        }
    };

    render(): ReactNode {
        const { options, value, menuIsOpen } = this.state;
        const {
            autoFocus,
            selectedValue,
            placeholder,
            getLabelToCompare,
            customDropdownClass,
            optionsRenderer,
            customDropdownWidth,
            containerIdToAttachDropdown,
            containerSelectorToAttachDropdown,
            className,
            placement,
            disabled,
            customStyle,
        } = this.props;

        return (
            <AutoComplete
                autoFocus={autoFocus}
                allowClear
                onClear={this.onClear}
                ref={this.ref}
                style={customStyle || { width: '100%' }}
                onSelect={this.onSelect}
                onSearch={this.onSearch}
                placeholder={placeholder}
                value={value}
                defaultActiveFirstOption={true}
                onChange={this.onChange}
                filterOption={false}
                onBlur={this.onBlur}
                onFocus={this.onFocus}
                open={menuIsOpen}
                className={'autosuggest' + (className ? ' ' + className : '')}
                dropdownClassName={customDropdownClass}
                dropdownMatchSelectWidth={customDropdownWidth}
                onKeyDown={this.onKeyDown}
                placement={placement}
                disabled={disabled}
                getPopupContainer={() =>
                    containerSelectorToAttachDropdown
                        ? (document.querySelector(containerSelectorToAttachDropdown) as HTMLElement)
                        : containerIdToAttachDropdown
                        ? (document.getElementById(containerIdToAttachDropdown) as HTMLElement)
                        : document.body
                }
            >
                {options?.map((l, index) => (
                    <AutoComplete.Option
                        key={`${typeof l === 'object' ? l.id : l.toString()}-${index}`}
                        value={getLabelToCompare(l)}
                    >
                        {optionsRenderer ? optionsRenderer(l) : getLabelToCompare(l)}
                    </AutoComplete.Option>
                ))}
            </AutoComplete>
        );
    }
}

export default AntAutoCompleteV2;
